import { Resources } from '@shared/localizations/Resources';
import { PositionTableColumnId } from './PositionTableColumns';
import { AccountType } from '../../cache/Accounts/Account.types';
import { formatTruncatePriceByDefaultPrecisionWithCurrency, formatTruncatePriceWithCurrency } from '../../Formatters/AssetFormatter';
import { formatRound, formatTruncate } from '../../Formatters/NumberFormatter';
import { formatTruncatePrice, formatTruncatePriceByMaxPrecision, formatTruncatePriceByVariableTickSize } from '../../Formatters/PriceFormatter';
import { formatTicks } from '../../Formatters/TicksFormatter';
import { formatLeverage, formatOperationType, formatSLTPTrigger } from '../../Formatters/TradingFormatter';
import { InstrumentTypes, formatInstrumentType } from '../../Instruments/InstrumentTypes';
import { getProductTypeKey } from '../../Instruments/ProductType';
import { StringUtils } from '../../StringUtils';
import { DateTimeConvertor } from '../../Time/DateTimeConvertor';
import { DateTimeUtils } from '../../Time/DateTimeUtils';
import { OrderType } from '../../Trading/OrderType';
function getSLLPrice(position) {
    const slOrder = position.getSLOrder();
    if (isNullOrUndefined(slOrder) || slOrder.getOrderType() !== OrderType.StopLimit) {
        return NaN;
    }
    return slOrder.getLimitPrice();
}
function convertExpDateReal(expDateReal) {
    if (!isNullOrUndefined(expDateReal) && expDateReal.getFullYear() > 2000) {
        return DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(expDateReal);
    }
    else {
        return DateTimeUtils._ZeroTime;
    }
}
function formatString(formattedValue, defaultFormattedValue = '') {
    return isValidString(formattedValue) ? formattedValue : defaultFormattedValue;
}
function formatTruncatePriceForAccount(price, account) {
    const accountAsset = account.getAsset();
    if (isNullOrUndefined(accountAsset)) {
        return formatTruncatePriceByDefaultPrecisionWithCurrency(price, account.getServerCurrency());
    }
    return formatTruncatePriceWithCurrency(price, accountAsset);
}
function formatSLTPPrice(instrument, sltpPrice, sltpTrigger) {
    const slFormattedPrice = formatTruncatePriceByVariableTickSize(instrument, sltpPrice);
    const slTrigger = formatSLTPTrigger(sltpTrigger);
    if (isValidString(slTrigger)) {
        return `${slFormattedPrice} ${slTrigger}`;
    }
    return slFormattedPrice;
}
function formatCommission(value, position) {
    if (!isValidNumber(value) || !isFinite(value)) {
        return '';
    }
    const account = position.getAccount();
    const instrument = position.getInstrument();
    const isMultiAsset = account.getType() === AccountType.MultiAsset;
    const formatAsset = isMultiAsset ? instrument.getExp2Asset() : account.getAsset();
    if (isNullOrUndefined(formatAsset)) {
        const assetName = isMultiAsset ? instrument.getExp2() : account.getServerCurrency();
        return formatTruncatePriceByDefaultPrecisionWithCurrency(value, assetName);
    }
    else {
        return formatTruncatePriceWithCurrency(value, formatAsset);
    }
}
export function isQuantityColumn(columnId) {
    return columnId === PositionTableColumnId.Quantity || columnId === PositionTableColumnId.StartOfDayQty;
}
export function isCurrencyColumn(columnId) {
    return (columnId === PositionTableColumnId.GrossPl ||
        columnId === PositionTableColumnId.Swaps ||
        columnId === PositionTableColumnId.ExpositionUsd ||
        columnId === PositionTableColumnId.Commissions ||
        columnId === PositionTableColumnId.NetPl ||
        columnId === PositionTableColumnId.PositionValue);
}
export function getCurrencyValue(position, columnId, useAccountCurrency) {
    switch (columnId) {
        case PositionTableColumnId.GrossPl:
            return position.getGrossPnL(useAccountCurrency);
        case PositionTableColumnId.Swaps:
            return position.getSwaps();
        case PositionTableColumnId.ExpositionUsd:
            return position.getExpositionValue(useAccountCurrency);
        case PositionTableColumnId.Commissions:
            return position.GetCommissionFromSettlement(useAccountCurrency);
        case PositionTableColumnId.NetPl:
            return position.getNetPnL(useAccountCurrency);
        case PositionTableColumnId.PositionValue:
            return position.getPositionValue(useAccountCurrency);
        default:
            return undefined;
    }
}
export function getValue(position, columnId, appSettigs) {
    const account = position.getAccount();
    const instrument = position.getInstrument();
    const slOrder = position.getSLOrder();
    const tpOrder = position.getTPOrder();
    if (isCurrencyColumn(columnId)) {
        return getCurrencyValue(position, columnId, true);
    }
    switch (columnId) {
        case PositionTableColumnId.Symbol:
            return instrument.getName();
        case PositionTableColumnId.Account:
            return account.getAccountName();
        case PositionTableColumnId.Quantity:
            return appSettigs.isLotsMode() ? position.getAmountLots() : position.getAmountAbsolute();
        case PositionTableColumnId.OpenPrice:
            return position.getOpenPrice();
        case PositionTableColumnId.ExpDate:
            return convertExpDateReal(instrument.getExpDateReal());
        case PositionTableColumnId.StrikePrice:
            return instrument.getStrikePrice();
        case PositionTableColumnId.PositionId:
            return position.getPositionId();
        case PositionTableColumnId.OperationType:
            return position.getOperationType();
        case PositionTableColumnId.OpenTime:
            return DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(position.getOpenTime());
        case PositionTableColumnId.CurrentPrice:
            return position.getCurrentPrice();
        case PositionTableColumnId.Sl:
            return isNullOrUndefined(slOrder) ? NaN : slOrder.getPrice();
        case PositionTableColumnId.Tp:
            return isNullOrUndefined(tpOrder) ? NaN : tpOrder.getPrice();
        case PositionTableColumnId.Comment:
            return position.getComment();
        case PositionTableColumnId.SymbolType:
            return formatInstrumentType(instrument.getType(), instrument.isCFD());
        case PositionTableColumnId.Login:
            return account.getUserName();
        case PositionTableColumnId.SymbolDescription:
            return instrument.getDescription();
        case PositionTableColumnId.PlOffset:
            return position.getPLTicks();
        case PositionTableColumnId.SlValue:
            return position.getSLValue(true);
        case PositionTableColumnId.ProductType:
            return Resources.getResource(getProductTypeKey(position.getProductType(), instrument.isFutureOrOption() || instrument.getType() === InstrumentTypes.CFD_FUTURES));
        case PositionTableColumnId.AvailableForSell:
            return appSettigs.isLotsMode()
                ? position.getAmountAvailableForSellLots()
                : position.getAmountAvailableForSellAbsolute();
        case PositionTableColumnId.TradingExchange:
            return instrument.getTradingExchange();
        case PositionTableColumnId.Sll:
            return getSLLPrice(position);
        case PositionTableColumnId.StartOfDayQty:
            return appSettigs.isLotsMode()
                ? position.getStartOfDayAmountLots()
                : position.getStartOfDayAmountAbsolute();
        case PositionTableColumnId.AssetName:
            return instrument.getAssetName();
        case PositionTableColumnId.Leverage:
            return position.getLeverage();
        case PositionTableColumnId.AccruedInterest:
            return position.getAccruedInterest();
        case PositionTableColumnId.NetPlPercent:
            return position.getNetPnLPercentage(true);
        case PositionTableColumnId.StrategyId:
            return position.getStrategyId();
        case PositionTableColumnId.Close:
        case PositionTableColumnId.CloseViaOE:
            return '';
        default:
            return undefined;
    }
}
export function getFormattedValue(position, columnId, appSettigs) {
    const value = getValue(position, columnId, appSettigs);
    if (isNullOrUndefined(value)) {
        return '';
    }
    const account = position.getAccount();
    const instrument = position.getInstrument();
    const tpOrder = position.getTPOrder();
    const slOrder = position.getSLOrder();
    switch (columnId /* NOSONAR S1479 */) {
        case PositionTableColumnId.Quantity:
        case PositionTableColumnId.StartOfDayQty:
        case PositionTableColumnId.AvailableForSell:
            return formatRound(Math.abs(value), instrument.getAmountPrecision(appSettigs.isLotsMode(), account, position.getProductType()));
        case PositionTableColumnId.OpenPrice:
            return appSettigs.isRoundedAverageOpenPrice()
                ? formatTruncatePrice(instrument, value)
                : formatTruncatePriceByMaxPrecision(value);
        case PositionTableColumnId.ExpDate:
            return value instanceof Date && value.getTime() > 0 ? DateTimeUtils.FormatToDate(value) : '';
        case PositionTableColumnId.StrikePrice:
            return formatTruncatePriceByVariableTickSize(instrument, value);
        case PositionTableColumnId.OperationType:
            return formatOperationType(position.getOperationType(), true);
        case PositionTableColumnId.OpenTime:
            return DateTimeUtils.formatDate(value, 'DD.MM.YYYY  HH:mm:ss');
        case PositionTableColumnId.CurrentPrice:
            return formatTruncatePriceByVariableTickSize(instrument, value);
        case PositionTableColumnId.Sl:
            return isNullOrUndefined(slOrder)
                ? ''
                : formatSLTPPrice(instrument, slOrder.getPrice(), slOrder.getSLTrigger());
        case PositionTableColumnId.Tp:
            return isNullOrUndefined(tpOrder)
                ? ''
                : formatSLTPPrice(instrument, tpOrder.getPrice(), tpOrder.getTPTrigger());
        case PositionTableColumnId.GrossPl:
            return formatString(formatTruncatePriceForAccount(position.getGrossPnL(true), account), StringUtils.NOT_AVAILABLE);
        case PositionTableColumnId.Swaps:
            return formatTruncatePriceForAccount(value, account);
        case PositionTableColumnId.ExpositionUsd:
            return formatTruncatePriceForAccount(position.getExpositionValue(true), account);
        case PositionTableColumnId.Commissions:
            return formatCommission(position.GetCommissionFromSettlement(true), position);
        case PositionTableColumnId.PlOffset:
            return isValidNumber(value)
                ? formatTicks(value, instrument, appSettigs.getOffsetType())
                : StringUtils.NOT_AVAILABLE;
        case PositionTableColumnId.NetPl:
            return formatTruncatePriceForAccount(position.getNetPnL(true), account);
        case PositionTableColumnId.PositionValue:
            return formatTruncatePriceForAccount(position.getPositionValue(true), account);
        case PositionTableColumnId.SlValue:
            return formatTruncatePriceForAccount(value, account);
        case PositionTableColumnId.Sll:
            return formatTruncatePriceByVariableTickSize(instrument, value);
        case PositionTableColumnId.Leverage:
            return formatString(formatLeverage(value, true), StringUtils.NOT_AVAILABLE);
        case PositionTableColumnId.AccruedInterest:
            return formatString(formatTruncatePriceForAccount(position.getAccruedInterest(), account), StringUtils.NOT_AVAILABLE);
        case PositionTableColumnId.NetPlPercent:
            return formatString(formatTruncate(isValidNumber(value) ? value : 0, 2) + ' %', StringUtils.NOT_AVAILABLE);
        default:
            return value.toString();
    }
}
